import { makeStyles } from '@mui/styles';
import { Ring, Sphere } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import * as THREE from 'three';
import { EAttributeType, EAttributeUnit } from '../../@types/AttributeTypes';
import { EUnitType } from '../../@types/Enums';
import { AttachmentType } from '../../models/standard/AttachmentModel';
import AttributeModel from '../../models/standard/AttributeModel';
import HotspotModel from '../../models/standard/HotspotModel';
import { useExportContext } from '../../provider/ExportProvider';
import { usePlan } from '../../provider/PlanProvider';
import { useUnitContext } from '../../provider/UnitProvider';
import {
  attributeUnitDisplayMap,
  convertAttributeValue,
  getCorrespondingUnit,
  getLocalizedAttributeLabel,
  IMPERIAL_TO_METRIC, METRIC_SCALE_FACTORS
} from '../../utils/attributes/attributeUtils';
import RENDER_ORDERS from '../../utils/renderOrder';
import { getAttachmentSpriteTexture } from './AttachmentTextureLoader';
import RobotoText from './RobotoText';


const useStyles = makeStyles((theme) => ({
  card: {
    minWidth: '200px',
    maxWidth: '500px',
    borderRadius: 10,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2),
    background: theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    wordWrap: 'break-word',
  },
  cardContent: {
    textAlign: 'left',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginTop: theme.spacing(2),
  },
  textField: {
    marginBottom: theme.spacing(2),
  },
}));

interface HotspotProps {
  hotspot: HotspotModel;
  onClick: () => void;
}

const Hotspot: React.FC<HotspotProps> = ({ hotspot, onClick }) => {
  const classes = useStyles();
  const intl = useIntl();
  const { formatMessage, locale } = intl;
  const { unitType } = useUnitContext();
  const { planModel } = usePlan();

  const [hovered, setHovered] = useState(false);
  const [texture, setTexture] = useState<THREE.Texture | null>(null);
  const spriteRef = useRef<THREE.Sprite>(null!);
  const middleSphereRef = useRef<THREE.Mesh>(null!);
  const outlineRef = useRef<THREE.Mesh>(null!);
  const pulseSpeed = 2;
  const { invalidate } = useThree();
  const { isExporting } = useExportContext();

  const attachmentType = hotspot.attachment.type;

  // Region Logic
  const regionCode = planModel?.region ? String(planModel.region).toUpperCase() : 'DE';
  const isRegionUS = regionCode === 'US';
  const defaultsRegionKey = isRegionUS ? 'US' : 'DE';

  // Load texture
  useEffect(() => {
    let isMounted = true;
    getAttachmentSpriteTexture(attachmentType)
      .then((loadedTexture) => {
        if (isMounted) {
          setTexture(loadedTexture);
          invalidate();
        }
      })
      .catch((error) => {
        console.error('Failed to load texture:', error);
      });

    return () => {
      isMounted = false;
    };
  }, [attachmentType, invalidate]);

  // Pulsing animation
  useFrame((state) => {
    if (outlineRef.current && !isExporting) {
      const scaleValue =
        1.3 + Math.sin(state.clock.getElapsedTime() * pulseSpeed) * 0.1;
      outlineRef.current.scale.set(scaleValue, scaleValue, scaleValue);
      invalidate();
    }
  });

  const yesLabel = formatMessage({ id: 'AttributeList.yes', defaultMessage: 'Ja' });
  const noLabel = formatMessage({ id: 'AttributeList.no', defaultMessage: 'Nein' });

  // --- DISPLAY TEXT LOGIC ---
  const displayText = useMemo(() => {
    if (hotspot.attachment.type !== AttachmentType.ATTRIBUTE) {
      return null;
    }

    const attr = hotspot.attachment.data as AttributeModel;

    // 1. Localize Key
    const key = getLocalizedAttributeLabel(attr, locale);

    // 2. Determine Effective Value (Check DB -> Check Default)
    const defaultObj = attr.defaults?.[defaultsRegionKey] || attr.defaults?.GLOBAL;
    let effectiveVal = attr.value;

    // Resolve Default if empty
    if (effectiveVal === undefined || effectiveVal === null || effectiveVal === '') {
      if (attr.type === EAttributeType.BOOL) {
        effectiveVal = defaultObj?.value !== undefined && defaultObj.value !== null && String(defaultObj.value) !== '' ? String(defaultObj.value) : 'true';
      } else if (defaultObj?.value !== undefined && defaultObj?.value !== null) {
        effectiveVal = String(defaultObj.value);
      }
    }

    // If still empty (e.g. text without default), just show Key
    if (effectiveVal === undefined || effectiveVal === null || effectiveVal === '') {
      return key;
    }

    let valStr = String(effectiveVal);

    // 3. Format Value based on Type
    if (attr.type === EAttributeType.BOOL) {
      valStr = valStr === 'true' ? yesLabel : noLabel;
    } else if (attr.type === EAttributeType.NUMBER) {
      // Handle Units
      let storedUnit = attr.unit;
      if (!storedUnit) {
        const validationObj = attr.validation?.[defaultsRegionKey] || attr.validation?.GLOBAL || {};
        const valUnit = (validationObj as any).unit;
        storedUnit = valUnit || defaultObj?.unit;
      }

      if (storedUnit) {
        const isStorageMetric = !isRegionUS;
        const isDisplayMetric = unitType === EUnitType.METRIC;
        const isNativeMode = isStorageMetric === isDisplayMetric;

        let activeDisplayUnit = storedUnit as EAttributeUnit;

        // Check if we need to display a different unit (Metric vs Imperial)
        if (!isNativeMode) {
          activeDisplayUnit = getCorrespondingUnit(storedUnit as EAttributeUnit, !isDisplayMetric);
        }

        // Convert Value if Units differ
        if (activeDisplayUnit !== storedUnit) {
           const num = parseFloat(valStr);
           if (!isNaN(num)) {
             let converted = convertAttributeValue(num, storedUnit as EAttributeUnit, !isDisplayMetric);

             // MAGNITUDE FIX: Imperial Storage -> Metric Display
             if (isDisplayMetric && IMPERIAL_TO_METRIC[storedUnit as EAttributeUnit]) {
                 const scale = METRIC_SCALE_FACTORS[activeDisplayUnit] || 1;
                 converted = converted / scale;
             }

             // Round for display (3 decimals standard)
             valStr = String(Math.round(converted * 1000) / 1000);
           }
        }

        const unitLabel = attributeUnitDisplayMap[activeDisplayUnit] || activeDisplayUnit;
        valStr += ` ${unitLabel}`;
      }
    }

    return `${key}: ${valStr}`;
  }, [
    hotspot.attachment,
    locale,
    unitType,
    defaultsRegionKey,
    isRegionUS,
    yesLabel,
    noLabel
  ]);

  if (!texture) return null;

  return (
    <group
      position={[hotspot.position.x, hotspot.position.y, 5]}
      renderOrder={RENDER_ORDERS.hotspot}
    >
      <sprite
        ref={spriteRef}
        position={[0, 0, 3.5]}
        scale={[1.35, 1.35, 1.5]} 
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.stopPropagation();
          onClick();
        }}
      >
        <spriteMaterial
          attach="material"
          map={texture}
          color={'#ffffff'}
          transparent={true}
          depthTest={false} 
        />
      </sprite>

      <mesh
        ref={middleSphereRef}
        position={[0, 0, 1]}
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
      >
        <Sphere args={[0.8, 50, 50]}>
          <meshBasicMaterial
            color={hovered ? '#40B7EA' : '#009fe3'}
            transparent={true}
            opacity={0.6}
            side={THREE.DoubleSide}
          />
        </Sphere>
      </mesh>

      <mesh
        ref={outlineRef}
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
      >
        <Ring args={[0.8, 0.9, 52]}>
          <meshBasicMaterial
            color={hovered ? '#40B7EA' : '#009fe3'}
            transparent={true}
            opacity={0.6}
            side={THREE.DoubleSide}
          />
        </Ring>
      </mesh>

      {displayText && (
        <RobotoText
          fontSize={0.6}
          position={[0, 1.5, 2]}
          color="black"
          message={displayText}
        />
      )}
    </group>
  );
};

export default Hotspot;
